// src/pages/builder/GameGeneratorPanel.js
//
// GAME GENERATOR — Layout Builder panel.
//
// Makes games from a topic's song lyrics: Multiple Choice (also Talk Choice,
// Typing Quiz, Class Quiz Show), Drag and Drop, Sorting Conveyor, Memory
// Match and Match Three, with emoji icons drawn from the words in the song.
// Free and offline — see services/gameGenerator.js for how.
//
// FLOW
// ----
//   Generate  -> a DRAFT in generatedGames/{slug}, all game types at once.
//   Edit      -> fix any question, answer, icon or pairing.
//   Try it    -> play the chosen game right here (nothing is recorded).
//   Publish   -> the chosen game type becomes the topic's live game in
//                quizzes/{slug}; whatever was there is backed up first.
//
// Nothing here reaches students until you press Publish.

import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAIN_GAMES, PLACEHOLDER } from "../../config/gameRegistry";
import BonusRouter from "../../components/quizzes/BonusRouter";
import { hasQuiz } from "../../data/quiz-library";
import {
  SUBJECTS,
  SUBJECT_LABELS,
  exactKey,
  getAllLibraryTopics,
  legacyKey,
  makeToolKey,
} from "../../services/topicLibraryService";
import {
  BONUS_OPTIONS,
  GAME_TYPES,
  bonusOptionsFor,
  familyOf,
  generateGames,
  lyricsHashOf,
  pickBonus,
  questionsForPublish,
  toLines,
} from "../../services/gameGenerator";
import { emojiFromImage, emojiImage } from "../../services/gameGeneratorEmoji";
import { getDrafts, getLiveGames, publishGame, saveDraft } from "../../services/gameGeneratorService";
import { GAME_PIC_LOOKS, GAME_PIC_QUALITIES, isRealPic, makeGamePicture, uploadGamePicture } from "../../services/gamePictureService";
import { PreviewFrame } from "./GameLibraryPanel";
import "./gameGenerator.css";

// Every row carries its topic's TOOL KEY as `slug` (makeToolKey in
// src/utils/topicKey.js): the old stripped slug for an ordinary topic, the
// topic's own exact slug when stripping would collide with another topic
// (earth'swater vs earthswater). Drafts and published games are written under
// that key and nowhere else, so two look-alike topics each get their own.
const labelOf = (gameType) => GAME_TYPES.find((g) => g.value === gameType)?.label || gameType;
const bonusLabel = (v) => BONUS_OPTIONS.find((b) => b.value === v)?.label || v || "—";
const sungCount = (lyrics) =>
  toLines(lyrics).filter((l) => {
    const s = l.trim();
    return s && !/^\s*[([].{1,40}[)\]]\s*:?\s*$/.test(s) && !/^\s*©/.test(s);
  }).length;

const clone = (x) => JSON.parse(JSON.stringify(x));

/* ------------------------------------------------------------------ */
/* Instructions                                                        */
/* ------------------------------------------------------------------ */

const Help = ({ open, onToggle }) => (
  <div className="gg-help">
    <button type="button" className="gg-help-toggle" onClick={onToggle}>
      <span>{open ? "▾" : "▸"}</span> How the game generator works
    </button>
    {open && (
      <ol className="gg-help-steps">
        <li>
          <strong>It reads the song lyrics</strong> in the Topic Library and makes several
          kinds of games at once: <em>Multiple Choice</em> (also used by Talk Choice, Typing
          Quiz and Class Quiz Show), <em>Drag and Drop</em>, <em>Sorting Conveyor</em>,{" "}
          <em>Memory Match</em> and <em>Match Three</em>. It picks 5–8 questions each time, with
          2 or 3 wrong answers, and adds emoji icons for words it recognises.
        </li>
        <li>
          <strong>Generate</strong> one topic, or press{" "}
          <kbd>🎲 Make drafts for every topic with no game</kbd>. Drafts are saved for you to
          check. <strong>Nothing reaches students until you press Publish.</strong>
        </li>
        <li>
          <strong>Open a draft</strong>, pick a game type, and fix anything you like: question
          wording, answers, which answer is right, icons. <kbd>↻ Swap</kbd> replaces a question
          with a different one from the same song, and <kbd>🎲 New version</kbd> remakes the
          whole draft.
        </li>
        <li>
          <strong>Pictures on cards and boxes.</strong> In Drag and Drop, Memory and Sorting, click a{" "}
          <kbd>＋🖼</kbd> box to make an AI picture (Image studio), upload your own, paste a link or use an
          emoji — or press <kbd>🎨 Make AI pictures for every round</kbd>. In Drag and Drop each round can
          show <em>words + picture</em> or <em>picture only</em>, on the cards and on the boxes separately.
          Pictures are saved small, in topic-images/games/. Save the draft to keep them.
        </li>
        <li>
          <strong>▶ Try it</strong> plays the game right here, exactly as students will see it.
          Nothing is recorded.
        </li>
        <li>
          <strong>Publish</strong> makes the game type you picked this topic’s live 🎮 Game,
          with the bonus round you chose (each topic gets a different one by default). If the
          topic already had a game, it is <strong>backed up first</strong> and then replaced.
        </li>
        <li>
          <strong>Publish several at once.</strong> Tick the box beside each{" "}
          <span className="gg-pill tone-amber">draft to check</span> you’ve checked (or{" "}
          <kbd>Select all ready drafts shown</kbd>), then press{" "}
          <kbd>🚀 Publish selected to students</kbd>. You confirm twice: once to review the list,
          once more to publish. Only drafts can be ticked, and a draft that still needs fixing
          can’t be ticked until you open it and fix it.
        </li>
        <li>
          <strong>New or changed lyrics?</strong> The list marks those topics{" "}
          <span className="gg-pill tone-amber">lyrics changed</span>. Open one and press{" "}
          <kbd>🎲 New version</kbd>. The Topic Library also has a{" "}
          <kbd>🎲 Make games from these lyrics</kbd> button under the lyrics box.
        </li>
        <li className="gg-muted">
          One thing still wins over a published game: questions typed directly into a topic on a
          page in the Layout Builder. Clear those if you want the generated game to show there.
        </li>
      </ol>
    )}
  </div>
);

/* ------------------------------------------------------------------ */
/* Small editors                                                       */
/* ------------------------------------------------------------------ */

/** Emoji icon box. Stores a data-URL image the games can show; empty = none. */
const IconBox = ({ value, onChange, title = "Icon (paste or type an emoji, or clear it)" }) => {
  const [text, setText] = useState(emojiFromImage(value));
  useEffect(() => setText(emojiFromImage(value)), [value]);
  return (
    <input
      className="gg-icon"
      value={text}
      title={title}
      placeholder="—"
      onChange={(e) => {
        const v = e.target.value.trim();
        setText(v);
        onChange(v ? emojiImage(v) : "");
      }}
    />
  );
};

const McEditor = ({ questions, spare, onChange, onSpare }) => {
  const set = (qi, patch) => onChange(questions.map((q, i) => (i === qi ? { ...q, ...patch } : q)));
  const setAnswer = (qi, ai, patch) =>
    set(qi, { answers: questions[qi].answers.map((a, j) => (j === ai ? { ...a, ...patch } : a)) });
  const markCorrect = (qi, ai) =>
    set(qi, { answers: questions[qi].answers.map((a, j) => ({ ...a, correct: j === ai })) });
  const remove = (qi) => onChange(questions.filter((_, i) => i !== qi));
  const swap = (qi) => {
    if (!spare.length) return;
    const [next, ...rest] = spare;
    onSpare([...rest, questions[qi]]);
    onChange(questions.map((q, i) => (i === qi ? next : q)));
  };
  const add = () => {
    if (!spare.length) return;
    const [next, ...rest] = spare;
    onSpare(rest);
    onChange([...questions, next]);
  };

  return (
    <div className="gg-qlist">
      {questions.map((q, qi) => (
        <div className="gg-q" key={qi}>
          <div className="gg-q-head">
            <span className="gg-q-num">{qi + 1}</span>
            <IconBox value={q.questionImage} onChange={(v) => set(qi, { questionImage: v || undefined })} />
            <textarea
              className="gg-q-text"
              rows={Math.min(3, Math.max(1, Math.ceil(String(q.question || "").length / 90)))}
              value={q.question}
              onChange={(e) => set(qi, { question: e.target.value })}
            />
            <div className="gg-q-actions">
              <button
                type="button"
                className="lb-btn lb-btn-ghost lb-btn-sm"
                onClick={() => swap(qi)}
                disabled={!spare.length}
                title={spare.length ? "Replace with another question from this song" : "No other questions left"}
              >
                ↻ Swap
              </button>
              <button type="button" className="lb-btn lb-btn-danger lb-btn-sm" onClick={() => remove(qi)} title="Delete question">
                ✕
              </button>
            </div>
          </div>
          <div className="gg-answers">
            {q.answers.map((a, ai) => (
              <label key={ai} className={`gg-answer ${a.correct ? "is-correct" : ""}`}>
                <input
                  type="radio"
                  name={`q${qi}`}
                  checked={Boolean(a.correct)}
                  onChange={() => markCorrect(qi, ai)}
                  title="This is the right answer"
                />
                <input
                  className="gg-answer-text"
                  value={a.text}
                  onChange={(e) => setAnswer(qi, ai, { text: e.target.value })}
                />
                {!a.correct && q.answers.length > 3 && (
                  <button
                    type="button"
                    className="gg-x"
                    onClick={() => set(qi, { answers: q.answers.filter((_, j) => j !== ai) })}
                    title="Remove this wrong answer"
                  >
                    ✕
                  </button>
                )}
              </label>
            ))}
            {q.answers.length < 4 && (
              <button
                type="button"
                className="lb-btn lb-btn-ghost lb-btn-sm"
                onClick={() => set(qi, { answers: [...q.answers, { text: "", correct: false }] })}
              >
                + wrong answer
              </button>
            )}
          </div>
        </div>
      ))}
      {questions.length < 8 && spare.length > 0 && (
        <button type="button" className="lb-btn lb-btn-soft lb-btn-sm" onClick={add}>
          + Add another question from the song
        </button>
      )}
    </div>
  );
};

const BIN_IN = "🎵 In the song";
const BIN_OUT = "🚫 Not in the song";

/* ------------------------------------------------------------------ */
/* Pictures on cards and boxes                                         */
/* ------------------------------------------------------------------ */

// A cell's picture can be: nothing, an emoji (made into a tiny SVG, as the
// generator has always done), or a real picture — AI-made, uploaded, or a
// pasted link (services/gamePictureService.js). All three are stored in the
// same field (image on the card side, targetImage on the box side), which is
// what the games already read.

/** The small thumbnail button in a table cell. Opens the picture editor. */
const PicCell = ({ value, open, onOpen, title }) => {
  const emoji = emojiFromImage(value);
  return (
    <button
      type="button"
      className={`gg-pic ${open ? "is-open" : ""} ${value ? "has-pic" : ""}`}
      onClick={onOpen}
      title={title}
      aria-expanded={open}
    >
      {emoji ? (
        <span className="gg-pic-emoji">{emoji}</span>
      ) : value ? (
        <img src={value} alt="" loading="lazy" />
      ) : (
        <span className="gg-pic-add">＋🖼</span>
      )}
    </button>
  );
};

/** The picture editor for one card or box (opens under its row). */
const PicEditor = ({ topic, word, value, side, look, quality, onChange, onClose }) => {
  const [idea, setIdea] = useState(String(word || "").replace(/_{2,}/g, "…"));
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [emoji, setEmoji] = useState(emojiFromImage(value));
  const fileRef = useRef(null);

  const run = async (kind, job) => {
    setBusy(kind);
    setError("");
    try {
      const url = await job();
      if (url) onChange(url);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="gg-piced">
      <div className="gg-piced-preview">
        {emojiFromImage(value) ? <span className="gg-pic-emoji is-big">{emojiFromImage(value)}</span> : value ? <img src={value} alt="" /> : <span className="gg-muted">No picture</span>}
        <small>{side === "box" ? "Box picture" : "Card picture"}</small>
      </div>

      <div className="gg-piced-tools">
        <div className="gg-piced-row">
          <label className="gg-piced-label">🎨 AI picture of</label>
          <input value={idea} onChange={(e) => setIdea(e.target.value)} placeholder="what to draw, e.g. a tall mountain" />
          <button
            type="button"
            className="lb-btn lb-btn-sm"
            disabled={!!busy || !idea.trim()}
            onClick={() => run("ai", async () => (await makeGamePicture({ topic, word: word || idea, idea, look, quality })).url)}
          >
            {busy === "ai" ? "Making…" : "Make it"}
          </button>
        </div>

        <div className="gg-piced-row">
          <label className="gg-piced-label">📁 From my computer</label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="gg-piced-file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) run("up", async () => (await uploadGamePicture({ topic, file, word: word || idea })).url);
            }}
          />
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" disabled={!!busy} onClick={() => fileRef.current?.click()}>
            {busy === "up" ? "Uploading…" : "Choose a file"}
          </button>
        </div>

        <div className="gg-piced-row">
          <label className="gg-piced-label">🔗 Picture link</label>
          <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://… (a picture already online)" />
          <button
            type="button"
            className="lb-btn lb-btn-ghost lb-btn-sm"
            disabled={!!busy || !/^https:\/\/\S+$/i.test(link.trim())}
            onClick={() => {
              onChange(link.trim());
              setLink("");
            }}
          >
            Use it
          </button>
        </div>

        <div className="gg-piced-row">
          <label className="gg-piced-label">😀 Emoji instead</label>
          <input
            className="gg-icon"
            value={emoji}
            placeholder="—"
            onChange={(e) => {
              const v = e.target.value.trim();
              setEmoji(v);
              onChange(v ? emojiImage(v) : "");
            }}
          />
          <span className="gg-piced-spacer" />
          {value && (
            <button
              type="button"
              className="lb-btn lb-btn-danger lb-btn-sm"
              disabled={!!busy}
              onClick={() => {
                setEmoji("");
                onChange("");
              }}
            >
              Remove picture
            </button>
          )}
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={onClose}>
            Done
          </button>
        </div>
        {error && <p className="gg-piced-error">{error}</p>}
      </div>
    </div>
  );
};

/** On/off pill pair for "show the words" on one side of a round. */
const WordsSwitch = ({ label, hide, onChange }) => (
  <span className="gg-words">
    <span className="gg-words-label">{label}</span>
    <button type="button" className={!hide ? "is-on" : ""} onClick={() => onChange(false)}>
      Words + picture
    </button>
    <button type="button" className={hide ? "is-on" : ""} onClick={() => onChange(true)}>
      Picture only
    </button>
  </span>
);

const OptionsEditor = ({ family, questions, onChange, topic }) => {
  const set = (qi, patch) => onChange(questions.map((q, i) => (i === qi ? { ...q, ...patch } : q)));
  const setOpt = (qi, oi, patch) =>
    set(qi, { options: questions[qi].options.map((o, j) => (j === oi ? { ...o, ...patch } : o)) });
  const removeOpt = (qi, oi) => set(qi, { options: questions[qi].options.filter((_, j) => j !== oi) });
  const addOpt = (qi) => {
    const opts = questions[qi].options;
    const id = `${family[0]}${qi + 1}-n${Date.now().toString(36)}`;
    const blank =
      family === "sorting"
        ? { id, text: "", targetText: BIN_IN }
        : family === "memory"
        ? { id, text: "", targetText: "", targetImage: "" }
        : { id, text: "", targetText: "" };
    set(qi, { options: [...opts, blank] });
  };

  // Boxes can carry a picture in Drag and Drop and Memory. Sorting's "boxes"
  // are the two bins, which have no picture of their own.
  const boxPics = family === "dragDrop" || family === "memory";
  const wordsSwitches = family === "dragDrop";
  const targetHead = family === "dragDrop" ? "Lyric it finishes" : family === "sorting" ? "Goes in" : "Its match";

  // which picture editor is open: "qi:oi:card" / "qi:oi:box"
  const [openPic, setOpenPic] = useState("");
  const [look, setLook] = useState("sbl");
  const [quality, setQuality] = useState("fast");
  const [sides, setSides] = useState("card");
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  const [bulk, setBulk] = useState(null); // { done, total, failed, current }
  const stopRef = useRef(false);

  // the bulk run reads the LATEST questions after every picture, so an edit
  // made while it runs isn't thrown away
  const latest = useRef(questions);
  latest.current = questions;
  const applyPic = (qi, oi, field, url) => {
    const qs = latest.current;
    onChange(qs.map((q, i) => (i === qi ? { ...q, options: q.options.map((o, j) => (j === oi ? { ...o, [field]: url } : o)) } : q)));
  };

  const jobs = () => {
    const out = [];
    latest.current.forEach((q, qi) =>
      (q.options || []).forEach((o, oi) => {
        if (sides !== "box" && String(o.text || "").trim() && (!onlyEmpty || !isRealPic(o.image)))
          out.push({ qi, oi, field: "image", word: o.text });
        if (boxPics && sides !== "card" && String(o.targetText || o.text || "").trim() && (!onlyEmpty || !isRealPic(o.targetImage)))
          out.push({ qi, oi, field: "targetImage", word: o.targetText || o.text });
      })
    );
    return out;
  };

  const makeAll = async () => {
    const list = jobs();
    if (!list.length) {
      setBulk({ done: 0, total: 0, failed: [], note: "Every card already has a picture." });
      return;
    }
    if (!window.confirm(`Make ${list.length} AI picture${list.length === 1 ? "" : "s"}? Each one is a paid call to Google's picture maker.`)) return;
    stopRef.current = false;
    const failed = [];
    for (let n = 0; n < list.length; n += 1) {
      if (stopRef.current) break;
      const j = list[n];
      setBulk({ done: n, total: list.length, failed, current: j.word });
      try {
        // eslint-disable-next-line no-await-in-loop
        const { url } = await makeGamePicture({ topic, word: j.word, look, quality });
        applyPic(j.qi, j.oi, j.field, url);
      } catch (e) {
        failed.push(`${j.word}: ${e?.message || e}`);
        if (/admin|deployed|limit/i.test(e?.message || "")) break; // the rest would fail the same way
      }
    }
    setBulk({ done: list.length, total: list.length, failed, finished: true, stopped: stopRef.current });
  };

  return (
    <div className="gg-qlist">
      <div className="gg-picbar">
        <strong>🎨 Pictures</strong>
        <label className="gg-field is-inline">
          Make for
          <select value={sides} onChange={(e) => setSides(e.target.value)} disabled={!!bulk && !bulk.finished}>
            <option value="card">{family === "memory" ? "Cards" : "Cards (drag side)"}</option>
            {boxPics && <option value="box">{family === "memory" ? "Partner cards" : "Boxes (drop side)"}</option>}
            {boxPics && <option value="both">Both sides</option>}
          </select>
        </label>
        <label className="gg-field is-inline">
          Look
          <select value={look} onChange={(e) => setLook(e.target.value)}>
            {GAME_PIC_LOOKS.map((l) => (
              <option key={l.key} value={l.key}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
        <label className="gg-field is-inline">
          Quality
          <select value={quality} onChange={(e) => setQuality(e.target.value)}>
            {GAME_PIC_QUALITIES.map((l) => (
              <option key={l.key} value={l.key}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
        <label className="gg-check">
          <input type="checkbox" checked={onlyEmpty} onChange={(e) => setOnlyEmpty(e.target.checked)} /> Only where there's no picture yet (emojis get replaced)
        </label>
        {bulk && !bulk.finished ? (
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => (stopRef.current = true)}>
            ■ Stop
          </button>
        ) : (
          <button type="button" className="lb-btn lb-btn-sm" onClick={makeAll}>
            🎨 Make AI pictures for every round
          </button>
        )}
        {bulk && (
          <span className={`gg-picbar-status ${bulk.failed?.length ? "is-warn" : ""}`}>
            {bulk.note
              ? bulk.note
              : !bulk.finished
              ? `Making ${bulk.done + 1} of ${bulk.total}: “${bulk.current}”…`
              : `${bulk.stopped ? "Stopped. " : "Done! "}${bulk.total - bulk.failed.length} made${bulk.failed.length ? `, ${bulk.failed.length} failed — ${bulk.failed[0]}` : ""}. Save the draft to keep them.`}
          </span>
        )}
        <p className="gg-muted gg-picbar-hint">
          Click any picture box below to make one AI picture (you can change what it draws), upload your own, paste a link, or use an emoji.
          {boxPics && " A picture on both sides that shows the same thing turns the round into picture matching."}
        </p>
      </div>

      {questions.map((q, qi) => (
        <div className="gg-q" key={qi}>
          <div className="gg-q-head">
            <span className="gg-q-num">{qi + 1}</span>
            <textarea
              className="gg-q-text"
              rows={1}
              value={q.question || ""}
              onChange={(e) => set(qi, { question: e.target.value })}
            />
          </div>
          {wordsSwitches && (
            <div className="gg-words-row">
              <WordsSwitch label="Cards show" hide={Boolean(q.hideCardText)} onChange={(v) => set(qi, { hideCardText: v || undefined })} />
              <WordsSwitch label="Boxes show" hide={Boolean(q.hideBoxText)} onChange={(v) => set(qi, { hideBoxText: v || undefined })} />
              {(q.hideCardText || q.hideBoxText) && (
                <span className="gg-muted">The words are still kept for read-aloud and the teacher's answer list.</span>
              )}
            </div>
          )}
          <table className="gg-opts">
            <thead>
              <tr>
                <th>Picture</th>
                <th>{family === "memory" ? "Card" : "Word"}</th>
                <th>{targetHead}</th>
                {boxPics && <th>{family === "memory" ? "Partner picture" : "Box picture"}</th>}
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {q.options.map((o, oi) => {
                const cardKey = `${qi}:${oi}:card`;
                const boxKey = `${qi}:${oi}:box`;
                const open = openPic === cardKey ? "card" : openPic === boxKey ? "box" : "";
                return (
                  <React.Fragment key={o.id || oi}>
                    <tr>
                      <td className="gg-pic-col">
                        <PicCell value={o.image} open={open === "card"} onOpen={() => setOpenPic(open === "card" ? "" : cardKey)} title="Card picture" />
                      </td>
                      <td>
                        <input value={o.text} onChange={(e) => setOpt(qi, oi, { text: e.target.value })} />
                      </td>
                      <td>
                        {family === "sorting" ? (
                          <select value={o.targetText} onChange={(e) => setOpt(qi, oi, { targetText: e.target.value })}>
                            <option value={BIN_IN}>{BIN_IN}</option>
                            <option value={BIN_OUT}>{BIN_OUT}</option>
                          </select>
                        ) : (
                          <input
                            className="gg-wide"
                            value={o.targetText}
                            placeholder={family === "memory" ? "(or just a picture →)" : ""}
                            onChange={(e) => setOpt(qi, oi, { targetText: e.target.value })}
                          />
                        )}
                      </td>
                      {boxPics && (
                        <td className="gg-pic-col">
                          <PicCell value={o.targetImage} open={open === "box"} onOpen={() => setOpenPic(open === "box" ? "" : boxKey)} title="Box picture" />
                        </td>
                      )}
                      <td>
                        {q.options.length > 3 && (
                          <button type="button" className="gg-x" onClick={() => removeOpt(qi, oi)} title="Remove">
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr className="gg-pic-row">
                        <td colSpan={boxPics ? 5 : 4}>
                          <PicEditor
                            key={openPic}
                            topic={topic}
                            side={open}
                            word={open === "card" ? o.text : o.targetText || o.text}
                            value={open === "card" ? o.image : o.targetImage}
                            look={look}
                            quality={quality}
                            onChange={(url) => (open === "card" ? applyPic(qi, oi, "image", url || undefined) : applyPic(qi, oi, "targetImage", url || ""))}
                            onClose={() => setOpenPic("")}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {q.options.length < 8 && (
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => addOpt(qi)}>
              + add a row
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

const Match3Editor = ({ questions, onChange }) => {
  const q = questions[0] || { tileTypes: [] };
  const tiles = q.tileTypes || [];
  const setTiles = (t) => onChange([{ ...q, tileTypes: t }]);
  return (
    <div className="gg-q">
      <p className="gg-muted">Tile pictures (at least 3). Type or paste emoji.</p>
      <div className="gg-tiles">
        {tiles.map((t, i) => (
          <span key={i} className="gg-tile">
            <input
              className="gg-icon"
              value={t}
              onChange={(e) => setTiles(tiles.map((x, j) => (j === i ? e.target.value.trim() : x)))}
            />
            {tiles.length > 3 && (
              <button type="button" className="gg-x" onClick={() => setTiles(tiles.filter((_, j) => j !== i))}>
                ✕
              </button>
            )}
          </span>
        ))}
        {tiles.length < 7 && (
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setTiles([...tiles, "⭐"])}>
            + tile
          </button>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Validation                                                          */
/* ------------------------------------------------------------------ */

const problemsFor = (content, gameType) => {
  const family = familyOf(gameType);
  const qs = content[family] || [];
  const out = [];
  if (!qs.length) out.push("There is nothing to play for this game type.");
  if (family === "mc") {
    if (qs.length < 3) out.push("Add at least 3 questions.");
    qs.forEach((q, i) => {
      if (!String(q.question || "").trim()) out.push(`Question ${i + 1} has no wording.`);
      const right = q.answers.filter((a) => a.correct);
      if (right.length !== 1) out.push(`Question ${i + 1} needs exactly one right answer.`);
      if (q.answers.filter((a) => !a.correct && String(a.text).trim()).length < 2)
        out.push(`Question ${i + 1} needs at least 2 wrong answers.`);
      if (q.answers.some((a) => !String(a.text).trim())) out.push(`Question ${i + 1} has an empty answer.`);
    });
  } else if (family === "match3") {
    if ((qs[0]?.tileTypes || []).filter(Boolean).length < 3) out.push("Match Three needs at least 3 tiles.");
  } else {
    qs.forEach((q, i) => {
      if ((q.options || []).length < 3) out.push(`Round ${i + 1} needs at least 3 rows.`);
      (q.options || []).forEach((o, j) => {
        if (!String(o.text).trim()) out.push(`Round ${i + 1}, row ${j + 1} has no word.`);
        if (family !== "memory" && !String(o.targetText).trim())
          out.push(`Round ${i + 1}, row ${j + 1} has nothing to match to.`);
        if (family === "memory" && !String(o.targetText).trim() && !o.targetImage)
          out.push(`Round ${i + 1}, row ${j + 1} needs a picture or a match.`);
        if (q.hideCardText && !o.image)
          out.push(`Round ${i + 1}, row ${j + 1}: cards are set to “picture only” but this card has no picture.`);
        if (q.hideBoxText && !o.targetImage)
          out.push(`Round ${i + 1}, row ${j + 1}: boxes are set to “picture only” but this box has no picture.`);
      });
    });
  }
  return out;
};

/* ------------------------------------------------------------------ */
/* Try it                                                              */
/* ------------------------------------------------------------------ */

const lazyCache = new Map();
const lazyGame = (gameType) => {
  if (!lazyCache.has(gameType)) {
    const entry = MAIN_GAMES.find((g) => g.id === gameType);
    if (!entry) return null;
    lazyCache.set(gameType, React.lazy(entry.load));
  }
  return lazyCache.get(gameType);
};

const TryIt = ({ gameType, bonusRoundType, content, title, onClose }) => {
  const [phase, setPhase] = useState("game");
  const [result, setResult] = useState("");
  const [full, setFull] = useState(false);
  const [run, setRun] = useState(0);
  const questions = useMemo(() => questionsForPublish(content, gameType), [content, gameType]);
  const Game = lazyGame(gameType);
  const topic = {
    slug: "__lab__",
    title,
    gameType,
    bonusRoundType,
    placeholderImage: PLACEHOLDER,
  };
  const done = (label) => (payload) => {
    const score = typeof payload === "number" ? payload : payload?.score ?? payload?.points ?? "";
    setResult(`${label} finished${score !== "" ? ` — score ${score}` : ""}.`);
    setPhase("done");
  };

  return (
    <div className={`gg-try ${full ? "is-full" : ""}`}>
      <div className="gg-try-bar">
        <strong>
          ▶ Trying: {labelOf(gameType)}
          {phase === "bonus" ? ` → bonus: ${bonusLabel(bonusRoundType)}` : ""}
        </strong>
        <span className="gg-muted">Practice only — nothing is saved.</span>
        <button
          type="button"
          className="lb-btn lb-btn-ghost lb-btn-sm"
          onClick={() => {
            setPhase("game");
            setRun((r) => r + 1);
          }}
        >
          ↻ Restart
        </button>
        <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setFull((v) => !v)}>
          {full ? "Exit full screen" : "⛶ Full screen"}
        </button>
        {bonusRoundType && bonusRoundType !== "none" && (
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setPhase("bonus")}>
            Try the bonus round
          </button>
        )}
        <button type="button" className="lb-btn lb-btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="gg-try-stage">
        {phase === "done" ? (
          <div className="gg-try-done">{result}</div>
        ) : !Game ? (
          <div className="gg-try-done">This game type can’t be previewed.</div>
        ) : (
          <PreviewFrame>
            <Suspense fallback={<div className="gg-try-done">Loading game…</div>}>
              {phase === "game" ? (
                <Game
                  key={`g-${gameType}-${run}`}
                  questions={questions}
                  topic={topic}
                  user={{ uid: "lab", displayName: "Game Generator", firstName: "Game" }}
                  placeholderImage={PLACEHOLDER}
                  onComplete={done("Game")}
                  onBonusComplete={done("Game")}
                />
              ) : (
                <BonusRouter
                  key={`b-${bonusRoundType}-${run}`}
                  slug="__lab__"
                  bonusRoundType={bonusRoundType}
                  questions={questions}
                  placeholderImage={PLACEHOLDER}
                  onComplete={done("Bonus round")}
                />
              )}
            </Suspense>
          </PreviewFrame>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Editor for one topic                                                */
/* ------------------------------------------------------------------ */

const Editor = ({ topic, draft, live, builtIn, onClose, onSaved }) => {
  // Already the tool key — set on the row. Re-stripping it here would put
  // earth'swater's game back on earthswater.
  const slug = topic.slug;
  const hasImage = Boolean(topic.imageUrl);
  const lyricsHash = lyricsHashOf(topic.lyrics);

  const fresh = useCallback(
    (count, seed) => {
      const g = generateGames(
        { slug, title: topic.title, lyrics: topic.lyrics, subject: topic.subject },
        { count, seed }
      );
      const firstType = GAME_TYPES.find((t) => g.available[t.value])?.value || "multipleChoice";
      return {
        ...g,
        gameType: firstType,
        bonusRoundType: pickBonus(slug, g.seed, firstType, hasImage),
        status: "draft",
      };
    },
    [slug, topic.title, topic.lyrics, topic.subject, hasImage]
  );

  const [work, setWork] = useState(() => (draft?.content ? clone(draft) : fresh()));
  const [dirty, setDirty] = useState(!draft?.content);
  const [trying, setTrying] = useState(false);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState(null);

  const lyricsChanged = Boolean(draft?.lyricsHash && draft.lyricsHash !== lyricsHash);
  const family = familyOf(work.gameType);
  const problems = problemsFor(work.content, work.gameType);
  const bonusChoices = bonusOptionsFor(work.gameType, hasImage);

  const update = (patch) => {
    setWork((w) => ({ ...w, ...patch }));
    setDirty(true);
  };
  const setFamily = (fam, qs) => update({ content: { ...work.content, [fam]: qs } });

  const regenerate = (count = work.count) => {
    if (dirty && draft?.content && !window.confirm("Make a new version? Your edits to this draft will be replaced.")) return;
    const next = fresh(count);
    // Keep the game type you'd picked if the new version supports it.
    if (next.available[work.gameType]) {
      next.gameType = work.gameType;
      next.bonusRoundType = bonusOptionsFor(work.gameType, hasImage).some((b) => b.value === work.bonusRoundType)
        ? work.bonusRoundType
        : next.bonusRoundType;
    }
    setWork(next);
    setDirty(true);
    setTrying(false);
    setNotice({ tone: "info", text: `New version made with ${next.content.mc.length} questions.` });
  };

  const pickType = (gt) => {
    const okBonus = bonusOptionsFor(gt, hasImage).some((b) => b.value === work.bonusRoundType);
    update({ gameType: gt, bonusRoundType: okBonus ? work.bonusRoundType : pickBonus(slug, work.seed, gt, hasImage) });
    setTrying(false);
  };

  const save = async () => {
    setBusy("save");
    try {
      const saved = await saveDraft(slug, {
        ...work,
        title: topic.title,
        subject: topic.subject,
        lyricsHash,
        status: draft?.status === "published" && !dirty ? "published" : "draft",
      });
      setDirty(false);
      onSaved({ ...saved, id: slug });
      setNotice({ tone: "ok", text: "Draft saved. Students don’t see it until you publish." });
    } catch (e) {
      setNotice({ tone: "error", text: `Could not save: ${e?.message || e}` });
    } finally {
      setBusy("");
    }
  };

  const publish = async () => {
    if (problems.length) return;
    const replacing = live
      ? `This replaces the live ${labelOf(live.gameType)} game (${live.count} questions). It is backed up first.\n\n`
      : builtIn
      ? "This topic has a built-in game from the app’s quiz library. The published game takes its place.\n\n"
      : "";
    if (!window.confirm(`${replacing}Publish ${labelOf(work.gameType)} for “${topic.title}” to students?`)) return;
    setBusy("publish");
    try {
      const res = await publishGame({
        slug,
        title: topic.title,
        topicImage: topic.imageUrl || "",
        draft: { ...work, title: topic.title, subject: topic.subject, lyricsHash },
        gameType: work.gameType,
        bonusRoundType: work.bonusRoundType,
      });
      setDirty(false);
      onSaved(
        { ...work, id: slug, slug, lyricsHash, status: "published" },
        { gameType: work.gameType, bonusRoundType: work.bonusRoundType, count: res.count, generated: true }
      );
      setNotice({
        tone: "ok",
        text: `Published! “${topic.title}” now plays ${labelOf(work.gameType)} (${res.count} ${
          family === "mc" ? "questions" : "rounds"
        }) with the ${bonusLabel(work.bonusRoundType)} bonus round.${res.backupId ? " The old game was backed up." : ""}`,
      });
    } catch (e) {
      setNotice({ tone: "error", text: `Could not publish: ${e?.message || e}` });
    } finally {
      setBusy("");
    }
  };

  const groups = [...new Set(GAME_TYPES.map((g) => g.group))];

  return (
    <div className="gg-editor">
      <div className="gg-editor-head">
        <button
          type="button"
          className="lb-btn lb-btn-ghost lb-btn-sm"
          onClick={() => {
            if (dirty && !window.confirm("Leave without saving this draft?")) return;
            onClose();
          }}
        >
          ← All topics
        </button>
        <div className="gg-editor-title">
          <h3>{topic.title || slug}</h3>
          <span className="gg-muted">
            {slug} · {sungCount(topic.lyrics)} lyric lines
          </span>
        </div>
        {dirty && <span className="gg-pill tone-amber">Not saved</span>}
        {!dirty && draft?.status === "published" && <span className="gg-pill tone-green">Published</span>}
      </div>

      {live && (
        <div className="gg-banner tone-blue">
          <strong>This topic already has a live game:</strong> {labelOf(live.gameType)}, {live.count}{" "}
          {familyOf(live.gameType) === "mc" ? (live.count === 1 ? "question" : "questions") : live.count === 1 ? "round" : "rounds"}
          {live.generated ? " (made here)" : ""}. It stays as it is unless you press Publish, and if
          you do, it is backed up first.
        </div>
      )}
      {!live && builtIn && (
        <div className="gg-banner tone-blue">
          This topic has a <strong>built-in game</strong> from the app’s quiz library. Publishing
          here replaces it for this topic.
        </div>
      )}
      {lyricsChanged && (
        <div className="gg-banner tone-amber">
          <strong>The lyrics changed</strong> since this draft was made. Press{" "}
          <strong>🎲 New version</strong> to make games from the new lyrics.
        </div>
      )}
      {(work.notes || []).map((n) => (
        <div key={n} className="gg-banner tone-grey">
          {n}
        </div>
      ))}
      {notice && (
        <div className={`gg-notice tone-${notice.tone}`}>
          {notice.text}
          <button type="button" className="gg-x" onClick={() => setNotice(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="gg-step">
        <div className="gg-step-head">
          <strong>1. Pick the game type</strong>
          <span className="gg-muted">All of these were made from this song. Greyed out = the lyrics can’t support it.</span>
        </div>
        {groups.map((grp) => (
          <div key={grp} className="gg-type-group">
            <span className="gg-type-label">{grp}</span>
            <div className="gg-types">
              {GAME_TYPES.filter((g) => g.group === grp).map((g) => (
                <button
                  key={g.value}
                  type="button"
                  className={`gg-type ${work.gameType === g.value ? "is-on" : ""}`}
                  disabled={!work.available?.[g.value]}
                  onClick={() => pickType(g.value)}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        ))}
        <div className="gg-row">
          <label className="gg-field">
            Bonus round
            <select value={work.bonusRoundType} onChange={(e) => update({ bonusRoundType: e.target.value })}>
              {bonusChoices.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <label className="gg-field">
            Questions
            <select value={work.count} onChange={(e) => regenerate(Number(e.target.value))}>
              {[5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="lb-btn lb-btn-soft" onClick={() => regenerate()}>
            🎲 New version
          </button>
        </div>
      </div>

      <div className="gg-step">
        <div className="gg-step-head">
          <strong>2. Check and edit — {labelOf(work.gameType)}</strong>
          <span className="gg-muted">
            {family === "mc"
              ? "Click the circle next to the right answer. The icon box shows a picture above the question — clear it to remove."
              : family === "dragDrop"
              ? "Each word is dragged onto the lyric line it finishes. Add pictures to either side, and choose words + picture or picture only for each round."
              : family === "sorting"
              ? "Each word rolls down the belt and goes in one of the two bins."
              : family === "memory"
              ? "Each card is matched with its partner card."
              : "These pictures fill the Match Three board."}
          </span>
        </div>
        {family === "mc" && (
          <McEditor
            questions={work.content.mc}
            spare={work.spare || []}
            onChange={(qs) => setFamily("mc", qs)}
            onSpare={(sp) => update({ spare: sp })}
          />
        )}
        {(family === "dragDrop" || family === "sorting" || family === "memory") && (
          <OptionsEditor family={family} questions={work.content[family]} onChange={(qs) => setFamily(family, qs)} topic={topic} />
        )}
        {family === "match3" && <Match3Editor questions={work.content.match3} onChange={(qs) => setFamily("match3", qs)} />}
      </div>

      {trying && (
        <TryIt
          key={`${work.gameType}-${work.seed}`}
          gameType={work.gameType}
          bonusRoundType={work.bonusRoundType}
          content={work.content}
          title={topic.title}
          onClose={() => setTrying(false)}
        />
      )}

      {problems.length > 0 && (
        <div className="gg-banner tone-red">
          <strong>Fix before publishing:</strong>
          <ul>
            {problems.slice(0, 6).map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="gg-savebar">
        <button type="button" className="lb-btn lb-btn-soft" onClick={() => setTrying((v) => !v)} disabled={problems.length > 0}>
          {trying ? "Hide game" : "▶ Try it"}
        </button>
        <span className="gg-muted gg-savebar-note">
          {labelOf(work.gameType)} · bonus: {bonusLabel(work.bonusRoundType)}
        </span>
        <button type="button" className="lb-btn lb-btn-ghost" onClick={save} disabled={busy !== ""}>
          {busy === "save" ? "Saving…" : "Save draft"}
        </button>
        <button type="button" className="lb-btn lb-btn-save" onClick={publish} disabled={busy !== "" || problems.length > 0}>
          {busy === "publish" ? "Publishing…" : "Publish to students"}
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

const FILTERS = [
  { key: "none", label: "No game yet" },
  { key: "has", label: "Has a game" },
  { key: "drafts", label: "Drafts to check" },
  { key: "all", label: "All" },
];

const store = {
  get(k, d) {
    try {
      const v = window.localStorage.getItem(k);
      return v === null ? d : JSON.parse(v);
    } catch {
      return d;
    }
  },
  set(k, v) {
    try {
      window.localStorage.setItem(k, JSON.stringify(v));
    } catch {
      /* ignore */
    }
  },
};

const GameGeneratorPanel = () => {
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(() => store.get("sbl.gamegen.help", true));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [topics, setTopics] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [live, setLive] = useState({});
  const [subject, setSubject] = useState("science");
  const [filter, setFilter] = useState("none");
  const [search, setSearch] = useState("");
  const [activeSlug, setActiveSlug] = useState(null);
  const [bulk, setBulk] = useState(null); // { done, total, current, label }
  // Publish selected: which drafts are ticked, which confirm step is showing
  // (0 = none, 1 = review the list, 2 = final check) and the last run's result.
  const [selected, setSelected] = useState(() => new Set());
  const [confirmStep, setConfirmStep] = useState(0);
  const [pubResult, setPubResult] = useState(null); // { ok: [], failed: [{ title, why }] }
  const panelRef = useRef(null);
  const pendingSlug = useRef(null);

  useEffect(() => store.set("sbl.gamegen.help", helpOpen), [helpOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [t, d, l] = await Promise.all([getAllLibraryTopics(), getDrafts(), getLiveGames()]);
      setTopics(t);
      setDrafts(d);
      setLive(l);
    } catch (e) {
      setError(
        e?.code === "permission-denied"
          ? "Permission denied. Deploy the updated firestore.rules (generatedGames and quizBackups blocks)."
          : `Could not load: ${e?.message || e}`
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !topics.length && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // "🎲 Make games from these lyrics" in the Topic Library lands here.
  useEffect(() => {
    const onOpen = (e) => {
      // The Topic Library sends the topic's real id. Rows are found by id OR
      // tool key, so this works before the rows for this load exist.
      const slug = exactKey(e.detail?.id || e.detail?.slug);
      if (!slug) return;
      pendingSlug.current = slug;
      setOpen(true);
      load().then(() => {
        setActiveSlug(pendingSlug.current);
        pendingSlug.current = null;
        setTimeout(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      });
    };
    window.addEventListener("sbl:open-game-generator", onOpen);
    return () => window.removeEventListener("sbl:open-game-generator", onOpen);
  }, [load]);

  const toolKey = useMemo(() => makeToolKey(topics), [topics]);

  const rows = useMemo(
    () =>
      topics
        .map((t) => {
          const id = exactKey(t.id || t.slug);
          const exact = exactKey(t.slug || t.id);
          const legacy = legacyKey(t.slug || t.id);
          const slug = toolKey(t);
          const lines = sungCount(t.lyrics);
          // This topic's OWN game. For an ordinary topic the exact-slug doc
          // counts too, because the game player reads it first.
          const liveGame = live[slug] || (slug === legacy ? live[exact] : null) || null;
          // A twin with no game of its own still PLAYS its look-alike's game
          // (the player falls back to the stripped slug). Shown, but counted
          // as "no game" so it gets its own draft.
          const sharedGame = !liveGame && slug !== legacy ? live[legacy] || null : null;
          const builtIn = !liveGame && !sharedGame && hasQuiz(legacy);
          const draft = drafts[slug] || null;
          const lyricsChanged = Boolean(draft?.lyricsHash && draft.lyricsHash !== lyricsHashOf(t.lyrics));
          return {
            topic: { ...t, slug },
            id,
            slug,
            legacy,
            lines,
            liveGame,
            sharedGame,
            builtIn,
            draft,
            lyricsChanged,
          };
        })
        .filter((r) => r.lines > 0),
    [topics, drafts, live, toolKey]
  );

  const hasGame = (r) => Boolean(r.liveGame || r.builtIn);
  const matches = (r) => {
    if (filter === "none") return !hasGame(r);
    if (filter === "has") return hasGame(r);
    if (filter === "drafts") return Boolean(r.draft && r.draft.status !== "published") || r.lyricsChanged;
    return true;
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => (r.topic.subject || "other") === subject)
      .filter(matches)
      .filter((r) => !q || String(r.topic.title || "").toLowerCase().includes(q) || r.slug.includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, subject, filter, search]);

  const counts = useMemo(() => {
    const out = {};
    SUBJECTS.forEach((s) => {
      const inS = rows.filter((r) => (r.topic.subject || "other") === s);
      out[s] = { total: inS.length, none: inS.filter((r) => !hasGame(r)).length };
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const noGame = rows.filter((r) => !hasGame(r));
  const needsDraft = noGame.filter((r) => !r.draft || r.lyricsChanged);

  const makeAllDrafts = async () => {
    if (!needsDraft.length) return;
    if (!window.confirm(`Make game drafts for ${needsDraft.length} topics with no game? Nothing goes live until you publish each one.`))
      return;
    setBulk({ done: 0, total: needsDraft.length, current: "" });
    const made = {};
    for (let i = 0; i < needsDraft.length; i += 1) {
      const r = needsDraft[i];
      setBulk({ done: i, total: needsDraft.length, current: r.topic.title || r.slug });
      try {
        const g = generateGames({ slug: r.slug, title: r.topic.title, lyrics: r.topic.lyrics, subject: r.topic.subject });
        const firstType = GAME_TYPES.find((t) => g.available[t.value])?.value || "multipleChoice";
        const saved = await saveDraft(r.slug, {
          ...g,
          title: r.topic.title,
          subject: r.topic.subject,
          gameType: firstType,
          bonusRoundType: pickBonus(r.slug, g.seed, firstType, Boolean(r.topic.imageUrl)),
          status: "draft",
        });
        made[r.slug] = { ...saved, id: r.slug };
      } catch (e) {
        // keep going; the row simply stays without a draft
      }
    }
    setDrafts((prev) => ({ ...prev, ...made }));
    setBulk(null);
    setFilter("drafts");
  };

  /* ---------- Publish selected ---------- */

  // Only a saved draft that is still "draft to check" can be ticked: not a
  // published one, not one whose lyrics changed since it was made.
  const isDraftToCheck = (r) =>
    Boolean(r.draft && r.draft.content && r.draft.gameType && r.draft.status !== "published" && !r.lyricsChanged);
  const draftProblems = (r) => (isDraftToCheck(r) ? problemsFor(r.draft.content, r.draft.gameType) : []);
  const canTick = (r) => isDraftToCheck(r) && draftProblems(r).length === 0;

  // Ticks are kept by slug; anything that stops qualifying (published,
  // lyrics changed) simply drops out of the selection.
  const picked = rows.filter((r) => selected.has(r.slug) && canTick(r));
  const visibleTickable = visible.filter(canTick);
  const allVisibleTicked = visibleTickable.length > 0 && visibleTickable.every((r) => selected.has(r.slug));

  const toggle = (slug) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  const tickAllVisible = (on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      visibleTickable.forEach((r) => (on ? next.add(r.slug) : next.delete(r.slug)));
      return next;
    });

  const replacesText = (r) => {
    if (r.liveGame) return `Replaces live ${labelOf(r.liveGame.gameType)} (${r.liveGame.count}) — backed up first`;
    if (r.builtIn) return "Replaces the built-in game";
    if (r.sharedGame) return `Gets its own game (now shares “${r.legacy}”)`;
    return "New game";
  };

  const publishPicked = async () => {
    const list = picked;
    if (!list.length) return;
    setConfirmStep(0);
    setPubResult(null);
    const ok = [];
    const failed = [];
    const draftPatch = {};
    const livePatch = {};
    for (let i = 0; i < list.length; i += 1) {
      const r = list[i];
      const title = r.topic.title || r.slug;
      setBulk({ done: i, total: list.length, current: title, label: "Publishing" });
      try {
        const d = r.draft;
        const res = await publishGame({
          slug: r.slug,
          title: r.topic.title,
          topicImage: r.topic.imageUrl || "",
          draft: { ...d, title: r.topic.title, subject: r.topic.subject, lyricsHash: d.lyricsHash || lyricsHashOf(r.topic.lyrics) },
          gameType: d.gameType,
          bonusRoundType: d.bonusRoundType,
        });
        draftPatch[r.slug] = { ...d, status: "published" };
        livePatch[r.slug] = {
          gameType: d.gameType,
          bonusRoundType: d.bonusRoundType,
          count: res.count,
          generated: true,
          id: r.slug,
        };
        ok.push(title);
      } catch (e) {
        failed.push({ title, why: e?.message || String(e) });
      }
    }
    setDrafts((prev) => ({ ...prev, ...draftPatch }));
    setLive((prev) => ({ ...prev, ...livePatch }));
    setSelected((prev) => {
      const next = new Set(prev);
      Object.keys(draftPatch).forEach((s) => next.delete(s));
      return next;
    });
    setBulk(null);
    setPubResult({ ok, failed });
  };

  const active = activeSlug
    ? rows.find((r) => r.slug === activeSlug) || rows.find((r) => r.id === activeSlug) || null
    : null;

  const statusCell = (r) => {
    if (r.liveGame)
      return (
        <span className={`gg-pill ${r.liveGame.generated ? "tone-green" : "tone-blue"}`}>
          {labelOf(r.liveGame.gameType)} · {r.liveGame.count}
          {r.liveGame.generated ? " · made here" : ""}
        </span>
      );
    if (r.builtIn) return <span className="gg-pill tone-blue">Built-in game</span>;
    if (r.sharedGame)
      return (
        <span
          className="gg-pill tone-grey"
          title={`Students on this topic play "${r.legacy}"’s game until you publish one for this topic.`}
        >
          No game of its own · shares “{r.legacy}”
        </span>
      );
    return <span className="gg-pill tone-grey">No game</span>;
  };

  const draftCell = (r) => {
    if (r.lyricsChanged) return <span className="gg-pill tone-amber">lyrics changed</span>;
    if (!r.draft) return <span className="gg-muted">—</span>;
    if (r.draft.status === "published") return <span className="gg-pill tone-green">published</span>;
    if (draftProblems(r).length) return <span className="gg-pill tone-red">needs a fix</span>;
    return <span className="gg-pill tone-amber">draft to check</span>;
  };

  return (
    <section className="lb-card gg-panel" ref={panelRef}>
      <div className="lb-card-head">
        <span className="lb-step gg-step-icon">🎲</span>
        <div>
          <h2>Game generator</h2>
          <p className="lb-hint">
            Make games from each topic’s song lyrics: questions with right and wrong answers, drag
            and drop, sorting, memory match and picture games. You check and publish them.
          </p>
        </div>
        <div className="gg-head-actions">
          {open && !active && (
            <button type="button" className="lb-btn lb-btn-soft" onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          )}
          <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Open"}
          </button>
        </div>
      </div>

      {open && (
        <>
          {error && <div className="gg-banner tone-red">{error}</div>}

          {active ? (
            <Editor
              key={active.slug}
              topic={active.topic}
              draft={active.draft}
              live={active.liveGame}
              builtIn={active.builtIn}
              onClose={() => setActiveSlug(null)}
              onSaved={(saved, liveEntry) => {
                setDrafts((prev) => ({ ...prev, [active.slug]: { ...(prev[active.slug] || {}), ...saved } }));
                if (liveEntry) setLive((prev) => ({ ...prev, [active.slug]: liveEntry }));
              }}
            />
          ) : (
            <>
              <Help open={helpOpen} onToggle={() => setHelpOpen((v) => !v)} />

              <div className="gg-summary">
                <span>
                  <strong>{rows.length}</strong> topics with lyrics
                </span>
                <span>
                  <strong>{noGame.length}</strong> have no game
                </span>
                <span>
                  <strong>{rows.filter((r) => r.draft && r.draft.status !== "published").length}</strong> drafts to
                  check
                </span>
                <button
                  type="button"
                  className="lb-btn"
                  onClick={makeAllDrafts}
                  disabled={!needsDraft.length || Boolean(bulk) || loading}
                >
                  🎲 Make drafts for every topic with no game ({needsDraft.length})
                </button>
              </div>

              {bulk && (
                <div className="gg-progress">
                  <div className="gg-progress-bar" style={{ width: `${Math.round((bulk.done / bulk.total) * 100)}%` }} />
                  <span>
                    {bulk.label || "Making drafts"}… {bulk.done} of {bulk.total}
                    {bulk.current ? ` — ${bulk.current}` : ""}
                  </span>
                </div>
              )}

              {pubResult && (
                <div className={`gg-notice tone-${pubResult.failed.length ? "error" : "ok"}`}>
                  {pubResult.ok.length > 0 && (
                    <div>
                      <strong>Published {pubResult.ok.length}</strong> game{pubResult.ok.length === 1 ? "" : "s"} to
                      students: {pubResult.ok.join(", ")}.
                    </div>
                  )}
                  {pubResult.failed.length > 0 && (
                    <div>
                      <strong>{pubResult.failed.length} could not be published</strong> (still drafts):
                      <ul>
                        {pubResult.failed.map((f) => (
                          <li key={f.title}>
                            {f.title} — {f.why}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <button type="button" className="gg-x" onClick={() => setPubResult(null)}>
                    ✕
                  </button>
                </div>
              )}

              <div className="gg-bulkbar">
                <button
                  type="button"
                  className="lb-btn lb-btn-ghost lb-btn-sm"
                  onClick={() => tickAllVisible(!allVisibleTicked)}
                  disabled={!visibleTickable.length || Boolean(bulk)}
                >
                  {allVisibleTicked ? "Untick drafts shown" : `Select all ready drafts shown (${visibleTickable.length})`}
                </button>
                {picked.length > 0 && (
                  <button
                    type="button"
                    className="lb-btn lb-btn-ghost lb-btn-sm"
                    onClick={() => setSelected(new Set())}
                    disabled={Boolean(bulk)}
                  >
                    Clear
                  </button>
                )}
                <span className="gg-muted">
                  <strong>{picked.length}</strong> selected
                </span>
                <button
                  type="button"
                  className="lb-btn lb-btn-save"
                  onClick={() => {
                    setPubResult(null);
                    setConfirmStep(1);
                  }}
                  disabled={!picked.length || Boolean(bulk) || confirmStep > 0}
                >
                  🚀 Publish selected to students ({picked.length})
                </button>
              </div>

              {confirmStep === 1 && (
                <div className="gg-confirm">
                  <div className="gg-confirm-head">
                    <strong>Check 1 of 2 — are these the right drafts?</strong>
                    <span className="gg-muted">
                      Each becomes its topic’s live 🎮 Game with the game type and bonus round saved in its draft.
                    </span>
                  </div>
                  <div className="gg-table-wrap">
                    <table className="gg-table">
                      <thead>
                        <tr>
                          <th>Topic</th>
                          <th>Game type</th>
                          <th>Bonus round</th>
                          <th>What happens</th>
                        </tr>
                      </thead>
                      <tbody>
                        {picked.map((r) => (
                          <tr key={r.slug}>
                            <td>
                              <div className="gg-topic">{r.topic.title || r.slug}</div>
                              <div className="gg-muted gg-slug">{r.slug}</div>
                            </td>
                            <td>{labelOf(r.draft.gameType)}</td>
                            <td>{bonusLabel(r.draft.bonusRoundType)}</td>
                            <td className={r.liveGame || r.builtIn ? "gg-warn-text" : ""}>{replacesText(r)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="gg-confirm-actions">
                    <button type="button" className="lb-btn lb-btn-ghost" onClick={() => setConfirmStep(0)}>
                      Cancel
                    </button>
                    <button type="button" className="lb-btn" onClick={() => setConfirmStep(2)}>
                      Yes, these are checked — continue
                    </button>
                  </div>
                </div>
              )}

              {confirmStep === 2 && (
                <div className="gg-confirm is-final">
                  <div className="gg-confirm-head">
                    <strong>
                      Check 2 of 2 — publish {picked.length} game{picked.length === 1 ? "" : "s"} to students now?
                    </strong>
                    <span>
                      Students will see {picked.length === 1 ? "it" : "them"} straight away.
                      {(() => {
                        const n = picked.filter((r) => r.liveGame || r.builtIn).length;
                        return n
                          ? ` ${n} of these replace${n === 1 ? "s" : ""} a game students already have (live games are backed up first).`
                          : " None of these replace an existing game.";
                      })()}
                    </span>
                  </div>
                  <div className="gg-confirm-actions">
                    <button type="button" className="lb-btn lb-btn-ghost" onClick={() => setConfirmStep(1)}>
                      ← Back to the list
                    </button>
                    <button type="button" className="lb-btn lb-btn-ghost" onClick={() => setConfirmStep(0)}>
                      Cancel
                    </button>
                    <button type="button" className="lb-btn lb-btn-save" onClick={publishPicked}>
                      Publish {picked.length} to students
                    </button>
                  </div>
                </div>
              )}

              <div className="gg-controls">
                <div className="gg-tabs">
                  {SUBJECTS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`gg-tab ${subject === s ? "is-on" : ""}`}
                      onClick={() => setSubject(s)}
                    >
                      {SUBJECT_LABELS[s]}
                      <span className="gg-tab-count">
                        {counts[s]?.none ?? 0} need games / {counts[s]?.total ?? 0}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="gg-tabs">
                  {FILTERS.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      className={`gg-tab gg-tab-sm ${filter === f.key ? "is-on" : ""}`}
                      onClick={() => setFilter(f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <input
                  className="gg-search"
                  placeholder="Search topics…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {loading && !rows.length ? (
                <p className="gg-muted">Loading topics…</p>
              ) : visible.length === 0 ? (
                <p className="gg-empty">No topics match. Try another subject or filter.</p>
              ) : (
                <div className="gg-table-wrap">
                  <table className="gg-table">
                    <thead>
                      <tr>
                        <th className="gg-check-col">
                          <input
                            type="checkbox"
                            aria-label="Tick every ready draft shown"
                            title="Tick every ready draft shown"
                            checked={allVisibleTicked}
                            disabled={!visibleTickable.length || Boolean(bulk)}
                            onChange={(e) => tickAllVisible(e.target.checked)}
                          />
                        </th>
                        <th>Topic</th>
                        <th>Lines</th>
                        <th>Live game now</th>
                        <th>Generated</th>
                        <th aria-label="Open" />
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((r) => (
                        <tr key={r.slug} className={selected.has(r.slug) && canTick(r) ? "is-picked" : ""}>
                          <td className="gg-check-col">
                            {isDraftToCheck(r) && (
                              <input
                                type="checkbox"
                                aria-label={`Select ${r.topic.title || r.slug}`}
                                checked={selected.has(r.slug) && canTick(r)}
                                disabled={!canTick(r) || Boolean(bulk)}
                                title={
                                  canTick(r)
                                    ? "Tick to publish with the others"
                                    : `Open it and fix first: ${draftProblems(r)[0] || ""}`
                                }
                                onChange={() => toggle(r.slug)}
                              />
                            )}
                          </td>
                          <td>
                            <div className="gg-topic">{r.topic.title || r.slug}</div>
                            <div className="gg-muted gg-slug">
                              {r.slug}
                              {r.topic.status !== "published" ? " · draft topic" : ""}
                            </div>
                          </td>
                          <td>{r.lines}</td>
                          <td>{statusCell(r)}</td>
                          <td>{draftCell(r)}</td>
                          <td className="gg-actions">
                            <button
                              type="button"
                              className={`lb-btn lb-btn-sm ${r.draft ? "lb-btn-soft" : ""}`}
                              onClick={() => setActiveSlug(r.slug)}
                            >
                              {r.draft ? "Open" : "🎲 Generate"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default GameGeneratorPanel;
